// Updated 2:38 PM
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  ArrowForward,
  Close,
  Home,
  Work,
  Person,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import { WebsiteSettings } from '../types';
import NotificationCenter from './NotificationCenter';
import { PROVIDER_NAV_ITEMS } from '../config/providerNavItems';
import { ADMIN_NAV_ITEMS } from '../config/adminNavItems';
import {
  ACCENT,
  DEEP,
  INK,
  LINE,
  MINT,
  MUTED,
  PAPER,
  primaryButtonSx,
  secondaryButtonSx,
} from './marketing/MarketingKit';

const PUBLIC_LINKS: Array<{ label: string; to: string }> = [
  { label: 'For providers', to: '/provider-benefits' },
  { label: 'For homeowners', to: '/customer-benefits' },
  { label: 'About', to: '/about-us' },
  { label: 'Demo', to: '/request-demo' },
];

const LOGO_SRC = '/hero/lawnhub-logo-circle.png';

/** Leaf mark cropped from the circular logo (518px source; leaves centered near 258,170). */
const BrandMark: React.FC<{ size?: number; bordered?: boolean }> = ({ size = 34, bordered }) => {
  const scale = size / 140;
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '9px',
        bgcolor: '#fff',
        border: bordered ? `1px solid ${LINE}` : 'none',
        backgroundImage: `url("${LOGO_SRC}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${518 * scale}px auto`,
        backgroundPosition: `${size / 2 - 258 * scale}px ${size / 2 - 170 * scale}px`,
      }}
    />
  );
};

const PublicNavbar: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menuOpen, setMenuOpen] = useState(false);
  const overHero = location.pathname === '/';

  const linkSx = (active: boolean) => ({
    position: 'relative',
    color: active ? '#fff' : 'rgba(255,255,255,0.82)',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: 500,
    py: 1,
    transition: 'color 150ms ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      borderRadius: 1,
      bgcolor: ACCENT,
      transform: active ? 'scaleX(1)' : 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform 180ms ease',
    },
    '&:hover': { color: '#fff' },
    '&:hover::after': { transform: 'scaleX(1)' },
  });

  const bar = (
    <Box
      component="header"
      sx={{
        bgcolor: overHero ? 'transparent' : DEEP,
        color: '#fff',
        borderBottom: `1px solid ${overHero ? 'rgba(255,255,255,0.16)' : 'transparent'}`,
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 3, height: { xs: 60, md: 72 } }}>
        <Box
          component={RouterLink}
          to="/"
          aria-label="LawnHub home"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: 'inherit', textDecoration: 'none', mr: 'auto' }}
        >
          <BrandMark />
          <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
            LawnHub
          </Typography>
        </Box>

        {isMobile ? (
          <IconButton aria-label="Open menu" onClick={() => setMenuOpen(true)} sx={{ color: 'inherit', mr: -1 }}>
            <MenuIcon />
          </IconButton>
        ) : (
          <>
            <Box component="nav" aria-label="Main" sx={{ display: 'flex', alignItems: 'center', gap: 3.5 }}>
              {PUBLIC_LINKS.map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Box key={l.to} component={RouterLink} to={l.to} aria-current={active ? 'page' : undefined} sx={linkSx(active)}>
                    {l.label}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ width: '1px', height: 22, bgcolor: 'rgba(255,255,255,0.22)' }} />
            <Box component={RouterLink} to="/login" sx={linkSx(location.pathname === '/login')}>
              Sign in
            </Box>
            <Button
              component={RouterLink}
              to="/get-started"
              variant="contained"
              disableElevation
              sx={{
                ...primaryButtonSx,
                fontSize: '0.95rem',
                px: 2.25,
                py: 0.9,
                bgcolor: '#fff',
                color: DEEP,
                '&:hover': { bgcolor: MINT, boxShadow: 'none' },
              }}
            >
              Get started free
            </Button>
          </>
        )}
      </Container>
    </Box>
  );

  return (
    <>
      {overHero ? (
        <Box sx={{ position: 'relative', height: 0, zIndex: 10 }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>{bar}</Box>
        </Box>
      ) : (
        bar
      )}

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{ sx: { width: 'min(320px, 86vw)', bgcolor: PAPER } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              height: 60,
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <BrandMark size={30} bordered />
              <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.02em', color: INK }}>
                LawnHub
              </Typography>
            </Box>
            <IconButton aria-label="Close menu" onClick={() => setMenuOpen(false)} sx={{ mr: -1, color: INK }}>
              <Close />
            </IconButton>
          </Box>
          <Box component="nav" aria-label="Main" sx={{ px: 2.5, py: 1 }}>
            {PUBLIC_LINKS.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Box
                  key={l.to}
                  component={RouterLink}
                  to={l.to}
                  onClick={() => setMenuOpen(false)}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    borderBottom: `1px solid ${LINE}`,
                    color: INK,
                    textDecoration: 'none',
                    fontSize: '1.1rem',
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {l.label}
                  <ArrowForward sx={{ fontSize: 18, color: active ? ACCENT : MUTED }} />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ mt: 'auto', p: 2.5, display: 'grid', gap: 1.25 }}>
            <Button
              component={RouterLink}
              to="/get-started"
              onClick={() => setMenuOpen(false)}
              variant="contained"
              sx={{ ...primaryButtonSx, bgcolor: DEEP, '&:hover': { bgcolor: INK, boxShadow: 'none' } }}
            >
              Get started free
            </Button>
            <Button component={RouterLink} to="/login" onClick={() => setMenuOpen(false)} variant="outlined" sx={secondaryButtonSx}>
              Sign in
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [websiteSettings, setWebsiteSettings] = useState<WebsiteSettings | null>(null);

  useEffect(() => {
    fetchWebsiteSettings();
  }, []);

  // Listen for custom events to refresh settings when updated
  useEffect(() => {
    const handleSettingsUpdate = () => {
      fetchWebsiteSettings();
    };

    window.addEventListener('websiteSettingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('websiteSettingsUpdated', handleSettingsUpdate);
  }, []);

  const fetchWebsiteSettings = async () => {
    try {
      const response = await apiService.get('/website-settings');
      if (response.success) {
        setWebsiteSettings(response.data as any);
      }
    } catch (err) {
      console.error('Failed to fetch website settings:', err);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    const isDemo =
      !!(user as any)?.isDemo || user?.email === 'demo.provider@illinoislawnhub.com';
    await logout();
    handleMenuClose();
    navigate(isDemo ? '/demo/exit' : '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const getDashboardPath = () => {
    if (user?.userType.toLowerCase() === 'customer') {
      return '/customer';
    } else if (user?.userType.toLowerCase() === 'provider') {
      return '/provider';
    } else if (user?.userType.toLowerCase() === 'admin') {
      return '/admin';
    }
    return '/';
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleMobileNav = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  if (!user) {
    return <PublicNavbar />;
  }

  return (
    <>
    <AppBar position="static" sx={{ backgroundColor: 'primary.main', overflow: 'hidden' }}>
      <Toolbar sx={{ position: 'relative', overflow: 'hidden', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
        <Box
          sx={{ 
            flexGrow: 1, 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
          onClick={() => navigate('/')}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
          >
            LawnHub
          </Typography>
        </Box>

        {isMobile ? (
          <IconButton
            color="inherit"
            aria-label="open menu"
            onClick={() => setMobileMenuOpen(true)}
            sx={{ ml: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {user.userType === 'customer' && (
              <Button
                color="inherit"
                startIcon={<Home />}
                onClick={() => navigate('/request-service')}
                sx={{ 
                  backgroundColor: isActive('/request-service') ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Service Requests
              </Button>
            )}
            
            <Button
              color="inherit"
              startIcon={
                user.userType === 'customer' ? <Person /> : 
                user.userType === 'provider' ? <Work /> : 
                <AdminPanelSettings />
              }
              onClick={() => navigate(getDashboardPath())}
              sx={{ 
                backgroundColor: location.pathname.startsWith(getDashboardPath()) ? 'rgba(255,255,255,0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              {user.userType.toLowerCase() === 'customer' ? 'MY SERVICES' : 
               user.userType.toLowerCase() === 'provider' ? 'MY DASHBOARD' : 
               'ADMIN HUB'}
            </Button>

            {user.userType?.toLowerCase() === 'admin' && (
              <Button
                color="inherit"
                onClick={() => navigate('/admin/financials')}
                sx={{
                  backgroundColor: location.pathname.startsWith('/admin/financials') ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Financials
              </Button>
            )}

            {/* Notification Center - Show for providers and admins */}
            {(user.userType === 'provider' || user.userType === 'admin') && (
              <NotificationCenter userType={user.userType} />
            )}

            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleNavigation('/profile')}>
                Profile
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/settings')}>
                Settings
              </MenuItem>
              {user.userType === 'provider' &&
                PROVIDER_NAV_ITEMS.filter(
                  (item) => !['/provider', '/profile', '/settings'].includes(item.path)
                ).map((item) => (
                  <MenuItem key={item.path} onClick={() => handleNavigation(item.path)}>
                    {item.label}
                  </MenuItem>
                ))}
              {user.userType === 'admin' &&
                ADMIN_NAV_ITEMS.filter(
                  (item) => !['/admin', '/profile', '/settings'].includes(item.path)
                ).map((item) => (
                  <MenuItem key={item.path} onClick={() => handleNavigation(item.path)}>
                    {item.label}
                  </MenuItem>
                ))}
              <MenuItem onClick={handleLogout}>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>

    {/* Mobile navigation drawer */}
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: 280 } }}
    >
      <Box sx={{ py: 2 }}>
          <List>
            {user.userType === 'customer' && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMobileNav('/request-service')}>
                  <ListItemText primary="Service Requests" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileNav(getDashboardPath())}>
                <ListItemText primary={user.userType === 'customer' ? 'My Services' : user.userType === 'provider' ? 'My Dashboard' : 'Admin Hub'} />
              </ListItemButton>
            </ListItem>
            {user.userType === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMobileNav('/admin/financials')}>
                  <ListItemText primary="Financials" />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleMobileNav('/profile'); setMobileMenuOpen(false); }}>
                <ListItemText primary="Profile" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileNav('/settings')}>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </ListItem>
            {user.userType === 'provider' &&
              PROVIDER_NAV_ITEMS.filter(
                (item) => !['/provider', '/profile', '/settings'].includes(item.path)
              ).map((item) => (
                <ListItem disablePadding key={item.path}>
                  <ListItemButton onClick={() => handleMobileNav(item.path)}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            {user.userType === 'admin' &&
              ADMIN_NAV_ITEMS.filter(
                (item) => !['/admin', '/profile', '/settings', '/admin/financials'].includes(item.path)
              ).map((item) => (
                <ListItem disablePadding key={item.path}>
                  <ListItemButton onClick={() => handleMobileNav(item.path)}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
      </Box>
    </Drawer>
    </>
  );
};

export default Navbar;
